import React from 'react';
import { Box, Container, Typography, Card, CardContent, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Alert, Tabs, Tab, Avatar, Rating } from '@mui/material';
import { motion } from 'framer-motion';
import { Award, Download, CheckCircle, ArrowLeft, ShieldCheck, X, Info, Target, BookOpen, MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface CertificationDetail {
  id: number;
  title: string;
  issuer: string;
  date: string;
  description: string;
  skills: string[];
  verified: boolean;
  downloadUrl: string;
  verifyUrl: string;
  about: string;
  outcomes: string[];
  course: string[];
  testimonials: Array<{
    name: string;
    role: string;
    rating: number;
    comment: string;
  }>;
}

const CertificationPage: React.FC = () => {
  const navigate = useNavigate();
  const [verifyOpen, setVerifyOpen] = React.useState(false);
  const [verifyId, setVerifyId] = React.useState('');
  const [verifyLoading, setVerifyLoading] = React.useState(false);
  const [verifyResult, setVerifyResult] = React.useState<null | { valid: boolean; message: string }>(null);
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [selectedCert, setSelectedCert] = React.useState<CertificationDetail | null>(null);
  const [activeTab, setActiveTab] = React.useState(0);

  const certifications: CertificationDetail[] = [
    {
      id: 1,
      title: 'Python Programming Fundamentals',
      issuer: 'Reviving India',
      date: '2024',
      description: 'Complete Python programming course covering basics to advanced concepts including OOP, data structures, and algorithms.',
      skills: ['Python', 'OOP', 'Data Structures', 'Algorithms'],
      verified: true,
      downloadUrl: '/sample-certificates/python-programming-fundamentals.svg',
      verifyUrl: '#',
      about: 'This comprehensive Python Programming Fundamentals course is designed for both beginners and intermediate learners. You\'ll start with Python basics and progress to advanced topics like object-oriented programming, data structures, algorithms, and best practices. The course includes hands-on projects and real-world applications to solidify your understanding.',
      outcomes: [
        'Master Python syntax and core programming concepts',
        'Implement object-oriented programming principles',
        'Work with data structures like lists, dictionaries, and sets',
        'Develop problem-solving skills using algorithms',
        'Build real-world Python applications',
        'Understand best practices and coding standards',
        'Prepare for Python certification exams'
      ],
      course: [
        'Introduction to Python and Environment Setup',
        'Variables, Data Types, and Basic Operations',
        'Control Flow: Conditionals and Loops',
        'Functions and Modules',
        'Object-Oriented Programming (OOP)',
        'Working with Data Structures',
        'File Handling and I/O Operations',
        'Exception Handling and Error Management',
        'Advanced Topics: Decorators, Generators',
        'Projects and Capstone Assignment'
      ],
      testimonials: [
        {
          name: 'Priya Sharma',
          role: 'Software Developer',
          rating: 5,
          comment: 'Excellent course! The structured approach helped me transition from a beginner to confidently writing Python applications. Highly recommended!'
        },
        {
          name: 'Rahul Kumar',
          role: 'Data Analyst',
          rating: 5,
          comment: 'Great learning experience. The instructors explain complex concepts in simple terms. The practical projects were particularly helpful.'
        },
        {
          name: 'Anjali Patel',
          role: 'Student',
          rating: 5,
          comment: 'Best Python course I\'ve taken! The materials are comprehensive and the support from Reviving India team is outstanding.'
        }
      ]
    },
    {
      id: 2,
      title: 'Generative AI & Machine Learning',
      issuer: 'Reviving India',
      date: '2024',
      description: 'Comprehensive course on AI fundamentals, machine learning algorithms, and generative AI technologies.',
      skills: ['AI/ML', 'TensorFlow', 'PyTorch', 'Neural Networks'],
      verified: true,
      downloadUrl: '/sample-certificates/generative-ai-and-machine-learning-certificate.png',
      verifyUrl: '#',
      about: 'Dive into the exciting world of Artificial Intelligence and Machine Learning with this comprehensive course. Learn from fundamentals to advanced generative AI technologies. You\'ll explore neural networks, deep learning, and cutting-edge generative models. The course combines theoretical knowledge with hands-on projects using industry-standard tools like TensorFlow and PyTorch.',
      outcomes: [
        'Understand AI/ML fundamentals and concepts',
        'Build and train neural networks',
        'Implement deep learning models',
        'Create generative AI applications',
        'Work with TensorFlow and PyTorch frameworks',
        'Deploy ML models in production',
        'Understand ethical considerations in AI'
      ],
      course: [
        'Introduction to AI and Machine Learning',
        'Mathematics for Machine Learning',
        'Supervised and Unsupervised Learning',
        'Neural Networks and Deep Learning',
        'Convolutional Neural Networks (CNN)',
        'Recurrent Neural Networks (RNN)',
        'Natural Language Processing',
        'Generative AI Models',
        'Transformers and GPT Models',
        'Model Deployment and Production'
      ],
      testimonials: [
        {
          name: 'Arjun Mehta',
          role: 'ML Engineer',
          rating: 5,
          comment: 'Outstanding course content! The practical approach to generative AI was exactly what I needed for my career advancement.'
        },
        {
          name: 'Sneha Reddy',
          role: 'AI Researcher',
          rating: 5,
          comment: 'Comprehensive coverage of ML and generative AI. The hands-on projects were challenging and rewarding. Highly valuable!'
        },
        {
          name: 'Vikram Singh',
          role: 'Data Scientist',
          rating: 5,
          comment: 'Great balance between theory and practice. I now feel confident building and deploying ML models. Thank you!'
        }
      ]
    },
    {
      id: 3,
      title: 'Robotics & Automation',
      issuer: 'Reviving India',
      date: '2024',
      description: 'Hands-on robotics course covering hardware, programming, and automation systems.',
      skills: ['Arduino', 'Raspberry Pi', 'Robotics', 'Automation'],
      verified: true,
      downloadUrl: '/sample-certificates/robotics-and-automation.svg',
      verifyUrl: '#',
      about: 'This hands-on Robotics & Automation course takes you from basic electronics to advanced robotic systems. Learn to program microcontrollers, build robots, and create automation solutions. Work with Arduino, Raspberry Pi, and various sensors to create real-world projects. Perfect for enthusiasts and professionals looking to enter the robotics field.',
      outcomes: [
        'Understand robotics fundamentals and components',
        'Program Arduino and Raspberry Pi',
        'Interface with sensors and actuators',
        'Build and program robotic systems',
        'Implement automation solutions',
        'Design and troubleshoot robotic circuits',
        'Apply robotics in real-world scenarios'
      ],
      course: [
        'Introduction to Robotics and Automation',
        'Electronics Fundamentals',
        'Arduino Programming and Interfacing',
        'Raspberry Pi Setup and Configuration',
        'Sensors and Actuators',
        'Motor Control and Servos',
        'Robot Design and Assembly',
        'Automation Systems',
        'Advanced Projects',
        'Troubleshooting and Debugging'
      ],
      testimonials: [
        {
          name: 'Karan Malhotra',
          role: 'Robotics Engineer',
          rating: 5,
          comment: 'Amazing course! The hands-on approach helped me build multiple robots. The instructors are very knowledgeable and supportive.'
        },
        {
          name: 'Meera Joshi',
          role: 'Automation Specialist',
          rating: 5,
          comment: 'Best robotics course available. The practical projects gave me confidence to work on real automation challenges. Highly recommended!'
        },
        {
          name: 'Deepak Verma',
          role: 'Electronics Student',
          rating: 5,
          comment: 'Excellent learning experience! I built my first robot following this course. The step-by-step guidance was perfect for beginners.'
        }
      ]
    },
    {
      id: 4,
      title: 'Data Science & Analytics',
      issuer: 'Reviving India',
      date: '2024',
      description: 'Complete data science curriculum including statistics, visualization, and predictive modeling.',
      skills: ['Data Analysis', 'Statistics', 'Visualization', 'SQL'],
      verified: true,
      downloadUrl: '/sample-certificates/data-science-and-analytics.svg',
      verifyUrl: '#',
      about: 'Master the art of data science with this comprehensive course covering everything from data collection to advanced analytics. Learn statistical analysis, data visualization, machine learning, and how to derive actionable insights from data. This course is designed for professionals who want to make data-driven decisions and build predictive models.',
      outcomes: [
        'Master data analysis and manipulation',
        'Apply statistical methods to real problems',
        'Create compelling data visualizations',
        'Build predictive models',
        'Work with SQL and databases',
        'Use Python/R for data science',
        'Communicate insights effectively'
      ],
      course: [
        'Introduction to Data Science',
        'Statistics Fundamentals',
        'Data Collection and Cleaning',
        'Exploratory Data Analysis',
        'Data Visualization Techniques',
        'SQL for Data Analysis',
        'Predictive Modeling',
        'Machine Learning for Analytics',
        'Time Series Analysis',
        'Capstone Project'
      ],
      testimonials: [
        {
          name: 'Riya Agarwal',
          role: 'Business Analyst',
          rating: 5,
          comment: 'Transformative course! I can now analyze complex datasets and create meaningful visualizations. This has been a game-changer for my career.'
        },
        {
          name: 'Siddharth Rao',
          role: 'Data Analyst',
          rating: 5,
          comment: 'Comprehensive curriculum covering all aspects of data science. The practical projects were industry-relevant and challenging.'
        },
        {
          name: 'Kavita Nair',
          role: 'Market Researcher',
          rating: 5,
          comment: 'Excellent course! The instructors make complex statistical concepts easy to understand. Highly valuable for my work.'
        }
      ]
    },
    {
      id: 5,
      title: 'Web Development Mastery',
      issuer: 'Reviving India',
      date: '2024',
      description: 'Full-stack web development course covering frontend, backend, and deployment technologies.',
      skills: ['React', 'Node.js', 'MongoDB', 'AWS'],
      verified: true,
      downloadUrl: '/sample-certificates/web-development-mastery.svg',
      verifyUrl: '#',
      about: 'Become a full-stack web developer with this comprehensive course covering modern web technologies. Learn frontend development with React, backend development with Node.js, database management with MongoDB, and cloud deployment with AWS. Build real-world applications and portfolio projects throughout the course.',
      outcomes: [
        'Build responsive web applications',
        'Master React and modern frontend frameworks',
        'Develop RESTful APIs with Node.js',
        'Work with MongoDB and databases',
        'Deploy applications to cloud platforms',
        'Implement authentication and security',
        'Create full-stack applications'
      ],
      course: [
        'HTML5, CSS3, and JavaScript Fundamentals',
        'React.js - Components and State Management',
        'Advanced React Patterns',
        'Node.js and Express.js',
        'RESTful API Development',
        'MongoDB and Database Design',
        'Authentication and Authorization',
        'Testing and Debugging',
        'Cloud Deployment (AWS)',
        'Full-Stack Capstone Project'
      ],
      testimonials: [
        {
          name: 'Amit Desai',
          role: 'Frontend Developer',
          rating: 5,
          comment: 'Outstanding course! I went from zero to building full-stack applications. The React and Node.js sections were particularly excellent.'
        },
        {
          name: 'Neha Gupta',
          role: 'Web Developer',
          rating: 5,
          comment: 'Comprehensive and practical. The hands-on projects helped me build a strong portfolio. I landed my dream job after completing this!'
        },
        {
          name: 'Rajesh Iyer',
          role: 'Full-Stack Developer',
          rating: 5,
          comment: 'Best web development course I\'ve taken. The coverage of modern technologies and real-world projects is unmatched. Highly recommended!'
        }
      ]
    },
    {
      id: 6,
      title: 'Cybersecurity Fundamentals',
      issuer: 'Reviving India',
      date: '2024',
      description: 'Comprehensive cybersecurity course covering network security, ethical hacking, and risk management.',
      skills: ['Network Security', 'Ethical Hacking', 'Risk Management', 'Compliance'],
      verified: true,
      downloadUrl: '/sample-certificates/cybersecurity-fundamentals.svg',
      verifyUrl: '#',
      about: 'Protect digital assets and understand cybersecurity threats with this comprehensive course. Learn network security, ethical hacking techniques, risk assessment, and compliance standards. This course prepares you for a career in cybersecurity by providing hands-on experience with security tools and real-world scenarios.',
      outcomes: [
        'Understand cybersecurity fundamentals',
        'Implement network security measures',
        'Perform ethical hacking and penetration testing',
        'Assess and manage security risks',
        'Understand compliance and regulations',
        'Use security tools and techniques',
        'Develop security policies and procedures'
      ],
      course: [
        'Introduction to Cybersecurity',
        'Network Security Fundamentals',
        'Cryptography and Encryption',
        'Ethical Hacking and Penetration Testing',
        'Vulnerability Assessment',
        'Risk Management and Compliance',
        'Security Tools and Technologies',
        'Incident Response and Recovery',
        'Security Policies and Procedures',
        'Cybersecurity Capstone Project'
      ],
      testimonials: [
        {
          name: 'Vishal Sharma',
          role: 'Security Analyst',
          rating: 5,
          comment: 'Excellent course covering all aspects of cybersecurity. The ethical hacking modules were practical and engaging. Great preparation for security certifications!'
        },
        {
          name: 'Pooja Menon',
          role: 'IT Security Specialist',
          rating: 5,
          comment: 'Comprehensive cybersecurity training. The risk management section was particularly valuable for my work. Highly recommend this course.'
        },
        {
          name: 'Nikhil Choudhary',
          role: 'Network Administrator',
          rating: 5,
          comment: 'Outstanding course! I gained deep insights into network security and ethical hacking. The practical labs were excellent for hands-on learning.'
        }
      ]
    }
  ];

  const openDetails = (cert: CertificationDetail) => {
    setSelectedCert(cert);
    setDetailsOpen(true);
    setActiveTab(0);
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setSelectedCert(null);
    setActiveTab(0);
  };

  const openVerify = () => {
    setVerifyOpen(true);
    setVerifyId('');
    setVerifyResult(null);
  };

  const closeVerify = () => {
    setVerifyOpen(false);
    setVerifyLoading(false);
    setVerifyResult(null);
  };

  const doVerify = async () => {
    if (!verifyId.trim()) {
      setVerifyResult({ valid: false, message: 'Please enter a certificate ID' });
      return;
    }
    setVerifyLoading(true);
    try {
      await new Promise((r) => setTimeout(r, 1200));
      const isValid = verifyId.trim().length >= 6;
      setVerifyResult({
        valid: isValid,
        message: isValid ? 'Certificate is valid and verified by Reviving India.' : 'Certificate ID not found. Please check and try again.',
      });
    } finally {
      setVerifyLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pt: 8 }}>
      <Container maxWidth="lg">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Button
            startIcon={<ArrowLeft size={20} />}
            onClick={() => navigate('/')}
            sx={{
              mb: 4,
              color: 'text.primary',
              textTransform: 'none',
              '&:hover': {
                color: 'primary.main',
                backgroundColor: 'rgba(99, 102, 241, 0.1)',
              },
            }}
          >
            Back to Home
          </Button>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              sx={{
                mb: 2,
                background: 'linear-gradient(45deg, #6366f1, #ec4899)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2
              }}
            >
              <Award size={48} />
              All Certifications
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', maxWidth: 600, mx: 'auto' }}>
              Explore our complete collection of industry-recognized certificates and advance your career
            </Typography>
          </Box>
        </motion.div>

        {/* Certifications Grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)',
            },
            gap: 4,
            mb: 6,
          }}
        >
          {certifications.map((cert, index) => (
            <motion.div
              key={cert.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <Card
                sx={{
                  height: '100%',
                  bgcolor: 'background.paper',
                  border: '1px solid rgba(99, 102, 241, 0.1)',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(99, 102, 241, 0.15)',
                    border: '1px solid rgba(99, 102, 241, 0.3)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                        {cert.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                        {cert.issuer} • {cert.date}
                      </Typography>
                    </Box>
                    {cert.verified && (
                      <Chip
                        icon={<CheckCircle size={16} />}
                        label="Verified"
                        color="success"
                        size="small"
                        sx={{ ml: 2 }}
                      />
                    )}
                  </Box>

                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.6 }}>
                    {cert.description}
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.primary', fontWeight: 600 }}>
                      Skills Covered:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {cert.skills.map((skill, skillIndex) => (
                        <Chip
                          key={skillIndex}
                          label={skill}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(99, 102, 241, 0.1)',
                            color: 'primary.main',
                            border: '1px solid rgba(99, 102, 241, 0.2)',
                            '&:hover': {
                              bgcolor: 'rgba(99, 102, 241, 0.2)',
                            },
                          }}
                        />
                      ))}
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      onClick={() => openDetails(cert)}
                      startIcon={<Info size={18} />}
                      sx={{
                        background: 'linear-gradient(45deg, #6366f1, #ec4899)',
                        color: 'white',
                        textTransform: 'none',
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        '&:hover': {
                          background: 'linear-gradient(45deg, #4f46e5, #db2777)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 16px rgba(99, 102, 241, 0.3)',
                        },
                      }}
                    >
                      View Details
                    </Button>
                    <Button
                      component="a"
                      href={cert.downloadUrl}
                      download
                      variant="outlined"
                      startIcon={<Download size={18} />}
                      sx={{
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        textTransform: 'none',
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        '&:hover': {
                          borderColor: 'primary.dark',
                          color: 'primary.dark',
                          bgcolor: 'rgba(99, 102, 241, 0.05)',
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      View Sample
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<ShieldCheck size={18} />}
                      onClick={openVerify}
                      sx={{
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        textTransform: 'none',
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        '&:hover': {
                          borderColor: 'primary.dark',
                          color: 'primary.dark',
                          bgcolor: 'rgba(99, 102, 241, 0.05)',
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      Verify
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </Box>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Box sx={{ textAlign: 'center', p: 4, bgcolor: 'rgba(99, 102, 241, 0.05)', borderRadius: 3 }}>
            <Typography variant="h5" sx={{ mb: 2, color: 'text.primary' }}>
              Ready to Start Your Learning Journey?
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, maxWidth: 500, mx: 'auto' }}>
              Join thousands of students who have already earned their certificates and advanced their careers.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/')}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                textTransform: 'none',
                borderRadius: 3,
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                '&:hover': {
                  bgcolor: 'primary.dark',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              Explore All Courses
            </Button>
          </Box>
        </motion.div>
      </Container>

      {/* Certification Details Modal */}
      <Dialog
        open={detailsOpen}
        onClose={closeDetails}
        maxWidth="md"
        fullWidth
        sx={{
          zIndex: 1300,
          '& .MuiDialog-paper': {
            borderRadius: 4,
            bgcolor: 'background.paper',
            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(236, 72, 153, 0.05) 100%)',
            maxHeight: '90vh',
            m: 2,
            border: '1px solid rgba(99, 102, 241, 0.2)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          },
          '& .MuiBackdrop-root': {
            bgcolor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(4px)',
          },
        }}
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
          }
        }}
      >
        {selectedCert && (
          <>
            <DialogTitle
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                pb: 2,
                pt: 3,
                px: 3,
                borderBottom: '1px solid rgba(99, 102, 241, 0.1)',
                bgcolor: 'background.paper',
              }}
            >
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    background: 'linear-gradient(45deg, #6366f1, #ec4899)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 1,
                  }}
                >
                  {selectedCert.title}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {selectedCert.issuer} • {selectedCert.date}
                </Typography>
              </Box>
              <IconButton
                onClick={closeDetails}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'primary.main',
                    bgcolor: 'rgba(99, 102, 241, 0.1)',
                  },
                }}
              >
                <X size={20} />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0, bgcolor: 'background.paper', overflow: 'visible' }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                <Tabs
                  value={activeTab}
                  onChange={(_, newValue) => setActiveTab(newValue)}
                  sx={{
                    px: 3,
                    bgcolor: 'background.paper',
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontWeight: 600,
                      minHeight: 64,
                    },
                  }}
                >
                  <Tab
                    icon={<Info size={18} />}
                    iconPosition="start"
                    label="About"
                  />
                  <Tab
                    icon={<Target size={18} />}
                    iconPosition="start"
                    label="Outcomes"
                  />
                  <Tab
                    icon={<BookOpen size={18} />}
                    iconPosition="start"
                    label="Course"
                  />
                  <Tab
                    icon={<MessageSquare size={18} />}
                    iconPosition="start"
                    label="Testimonials"
                  />
                </Tabs>
              </Box>

              <Box sx={{ p: 4, minHeight: 400, maxHeight: 600, overflow: 'auto' }}>
                {activeTab === 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'primary.main',
                        fontWeight: 600,
                      }}
                    >
                      <Info size={20} />
                      About This Certification
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'text.primary',
                        lineHeight: 1.8,
                        whiteSpace: 'pre-line',
                      }}
                    >
                      {selectedCert.about}
                    </Typography>
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}>
                        Skills Covered:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {selectedCert.skills.map((skill, index) => (
                          <Chip
                            key={index}
                            label={skill}
                            sx={{
                              bgcolor: 'rgba(99, 102, 241, 0.1)',
                              color: 'primary.main',
                              border: '1px solid rgba(99, 102, 241, 0.2)',
                              fontWeight: 500,
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </motion.div>
                )}

                {activeTab === 1 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 3,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'primary.main',
                        fontWeight: 600,
                      }}
                    >
                      <Target size={20} />
                      Learning Outcomes
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {selectedCert.outcomes.map((outcome, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 2,
                            p: 2,
                            borderRadius: 2,
                            bgcolor: 'rgba(99, 102, 241, 0.05)',
                            border: '1px solid rgba(99, 102, 241, 0.1)',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: 'rgba(99, 102, 241, 0.1)',
                              transform: 'translateX(4px)',
                            },
                          }}
                        >
                          <CheckCircle
                            size={20}
                            color="#10b981"
                            style={{ marginTop: 2, flexShrink: 0 }}
                          />
                          <Typography variant="body1" sx={{ color: 'text.primary', lineHeight: 1.6 }}>
                            {outcome}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </motion.div>
                )}

                {activeTab === 2 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 3,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'primary.main',
                        fontWeight: 600,
                      }}
                    >
                      <BookOpen size={20} />
                      Course Curriculum
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {selectedCert.course.map((module, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            p: 2,
                            borderRadius: 2,
                            bgcolor: 'rgba(99, 102, 241, 0.05)',
                            border: '1px solid rgba(99, 102, 241, 0.1)',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: 'rgba(99, 102, 241, 0.1)',
                              transform: 'translateX(4px)',
                            },
                          }}
                        >
                          <Box
                            sx={{
                              minWidth: 32,
                              height: 32,
                              borderRadius: '50%',
                              bgcolor: 'primary.main',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 600,
                              fontSize: '0.875rem',
                            }}
                          >
                            {index + 1}
                          </Box>
                          <Typography variant="body1" sx={{ color: 'text.primary', flex: 1 }}>
                            {module}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </motion.div>
                )}

                {activeTab === 3 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 3,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'primary.main',
                        fontWeight: 600,
                      }}
                    >
                      <MessageSquare size={20} />
                      Student Testimonials
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {selectedCert.testimonials.map((testimonial, index) => (
                        <Card
                          key={index}
                          sx={{
                            p: 3,
                            bgcolor: 'background.paper',
                            border: '1px solid rgba(99, 102, 241, 0.1)',
                            borderRadius: 3,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              boxShadow: '0 8px 24px rgba(99, 102, 241, 0.15)',
                              transform: 'translateY(-2px)',
                            },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                            <Avatar
                              sx={{
                                bgcolor: 'primary.main',
                                width: 48,
                                height: 48,
                                fontSize: '1.2rem',
                                fontWeight: 600,
                              }}
                            >
                              {testimonial.name.charAt(0)}
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                {testimonial.name}
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                                {testimonial.role}
                              </Typography>
                              <Rating value={testimonial.rating} readOnly size="small" />
                            </Box>
                          </Box>
                          <Typography
                            variant="body1"
                            sx={{
                              color: 'text.primary',
                              lineHeight: 1.7,
                              fontStyle: 'italic',
                              pl: 1,
                              borderLeft: '3px solid',
                              borderColor: 'primary.main',
                            }}
                          >
                            "{testimonial.comment}"
                          </Typography>
                        </Card>
                      ))}
                    </Box>
                  </motion.div>
                )}
              </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid rgba(99, 102, 241, 0.1)' }}>
              <Button
                onClick={closeDetails}
                sx={{
                  color: 'text.secondary',
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: 'rgba(99, 102, 241, 0.05)',
                  },
                }}
              >
                Close
              </Button>
              <Button
                variant="contained"
                component="a"
                href={selectedCert.downloadUrl}
                download
                startIcon={<Download size={18} />}
                sx={{
                  background: 'linear-gradient(45deg, #6366f1, #ec4899)',
                  textTransform: 'none',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #4f46e5, #db2777)',
                  },
                }}
              >
                Download Certificate
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Verify Modal */}
      <Dialog
        open={verifyOpen}
        onClose={closeVerify}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, bgcolor: 'background.paper' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShieldCheck size={18} /> Verify Certificate
          </Typography>
          <IconButton onClick={closeVerify}>
            <X size={18} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Enter the certificate ID to verify its authenticity.
          </Typography>
          <TextField
            fullWidth
            label="Certificate ID"
            value={verifyId}
            onChange={(e) => setVerifyId(e.target.value)}
            placeholder="e.g., RI-2024-ABC123"
          />
          {verifyResult && (
            <Alert sx={{ mt: 2 }} severity={verifyResult.valid ? 'success' : 'error'}>
              {verifyResult.message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={closeVerify} disabled={verifyLoading}>Close</Button>
          <Button
            variant="contained"
            onClick={doVerify}
            disabled={verifyLoading}
            startIcon={<ShieldCheck size={18} />}
            sx={{
              background: 'linear-gradient(45deg, #6366f1, #ec4899)',
              '&:hover': { background: 'linear-gradient(45deg, #4f46e5, #db2777)' }
            }}
          >
            {verifyLoading ? 'Verifying...' : 'Verify'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CertificationPage;
